import { useSearchParams } from "react-router-dom";
import MembersGrid from "@/components/social-meet/MembersGrid";
import NearbyPage from "@/pages/Nearby";
import SocialMeetSafetyNotice from "@/components/social-meet/SocialMeetSafetyNotice";
import { InviteFriendsButton } from "@/components/ShareInvite";

// Part 6 — sections: Global · New & Nearby · Live Now (+ Online, Map, Anonymous)
// Owner (Oct 2026): the Map is hidden from Social Meet but fully preserved —
// flip REACT_APP_SOCIAL_MEET_MAP to "on" to restore the tab (code untouched).
const MAP_ENABLED = process.env.REACT_APP_SOCIAL_MEET_MAP === "on";
const TABS = [
  { key: "all", label: "Global" },
  { key: "new_nearby", label: "New & Nearby" },
  { key: "live", label: "Live Now" },
  { key: "online", label: "Online" },
  ...(MAP_ENABLED ? [{ key: "nearby", label: "Map" }] : []),
  { key: "anon", label: "Anonymous" },
];
const LEGACY_TAB = { new: "new_nearby" };

// Social Meet — MyPandaX's single member-discovery destination.
// "Nearby" embeds the existing map experience untouched; every other tab
// reuses the shared MembersGrid (no duplicate member systems).
export default function SocialMeetPage() {
  const [params, setParams] = useSearchParams();
  const rawTab = LEGACY_TAB[params.get("tab")] || params.get("tab");
  const tab = TABS.some((t) => t.key === rawTab) ? rawTab : "all";

  const setTab = (key) => setParams((p) => { p.set("tab", key); return p; }, { replace: true });

  return (
    <div data-testid="social-meet-page" className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl">Social Meet</h1>
            <p className="text-sm text-zinc-500 mt-1">Find and connect with the vault.</p>
          </div>
          <InviteFriendsButton testid="members-invite-friends-btn" />
        </div>
        <SocialMeetSafetyNotice />
        <div data-testid="social-meet-tabs" role="tablist" aria-label="Discovery tabs" className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1 sticky top-0 z-40 bg-zinc-950/90 backdrop-blur-md">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`social-meet-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 min-h-[40px] text-xs uppercase tracking-widest px-4 py-2 border transition-colors ${tab === t.key
                ? "border-rose-600 bg-rose-600/15 text-rose-300"
                : "border-white/10 text-zinc-400 hover:bg-white/5"} ${t.key === "live" ? "text-red-400" : ""}`}
            >
              {t.key === "live" ? <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />{t.label}</span> : t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "nearby" ? (
        <NearbyPage />
      ) : (
        <div className="max-w-6xl mx-auto px-4 lg:px-8 pb-10">
          <MembersGrid filter={tab} />
        </div>
      )}
    </div>
  );
}
